import React, { useEffect, useState, useCallback, createContext, useContext, useRef } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
// import ThemeToggleButton from "../helper/ThemeToggleButton";
import { getUserProfile } from '../api/profileApi';
import { logoutUser } from '../auth/authApi';
import 'datatables.net-dt/js/dataTables.dataTables.js';
import 'datatables.net-responsive-dt/js/responsive.dataTables.js';
import { createSettings, getSettingsByOrg } from '../api/settingsAPI';
import { getSetupTasks } from '../api/setupTasksAPI';
import { getWorkOrderCountSummary } from '../api/work-orderAPI';
import { useAppSettings } from '../utils/AppSettingsContext';
import { registerErrorHandler } from '../utils/errorController';
import { ModuleProvider, useModule } from '../utils/useModule';
import './Loader.css';

// Exporter for user profile context
const UserProfileContext = createContext(null);

export const useUserProfile = () => {
  const context = useContext(UserProfileContext);
  if (context && typeof context === 'object' && 'userProfile' in context) {
    return context.userProfile;
  }
  return context;
};

export const useHasPageAccess = () => {
  const context = useContext(UserProfileContext);
  if (context && typeof context === 'object' && typeof context.hasPageAccess === 'function') {
    return context.hasPageAccess;
  }
  return () => true;
};

export const useOnboardingStatus = () => {
  const context = useContext(UserProfileContext);
  if (context && typeof context === 'object') {
    return {
      loaded: Boolean(context.setupCompletion?.loaded),
      showSetupPrompt: Boolean(context.showSetupPrompt),
      hasWorkOrderRecords: Boolean(context.hasWorkOrderRecords),
    };
  }
  return { loaded: false, showSetupPrompt: false, hasWorkOrderRecords: false };
};

const AUTO_COLLAPSED_ROUTES = [
  '/work-assignment',
  '/visual-planner',
  '/dashboards/downtime',
  '/dashboards/operational',
  '/dashboards/performance',
  '/dashboards/utilisation',
  '/dashboards/reliability',
  '/service-management',
  '/inspections-management',
  '/compliance-management',
  '/component-management',
  '/component-planner',
  '/asset-list',
  '/work-orders'
  //'/asset-list',
  //'/measaurements',
  //'/locations',
  //'/maintenance-plans',
  //'/purchase-orders',
  //'/stock-items',
  //'/asset-assignments',
  //'/visual-planner',
];

const BETA_RELEASE_PAGE = [
  '/work-assignment',
  '/component-planner'
];

const SETUP_TASK_KEYS = ['site', 'team', 'invite', 'asset', 'workorder'];
const LOGOUT_BROADCAST_KEY = 'samurai:logout:broadcast';
const LOGIN_BROADCAST_KEY = 'samurai:login:broadcast';
const PRODUCT_TOUR_TRIGGER_KEY = 'samurai:product-tour:trigger';
const PRODUCT_TOUR_TRIGGER_ONCE_KEY = 'samurai:product-tour:trigger-once';
const TOUR_GUIDE_SETTING = {
  name: 'tour_guide_enabled',
  description: 'Enable the onboarding tour on the home and setup pages.',
  value: 'true',
  type: 'checkbox',
  category: 'Experience',
};

const MasterLayoutBodyContent = ({ children, pageTitle, headerActions, headerRightActions }) => {
  const navigate = useNavigate();
  let [mobileMenu, setMobileMenu] = useState(false);
  const location = useLocation();
  const { appSettings, setAppSettings } = useAppSettings();
  // const { isLoading } = useLoading();
  const [userProfile, setUserProfile] = useState(null);
  const [userImage, setUserImage] = useState(null);
  const [name, setName] = useState('');
  const [database, setDatabase] = useState(null);
  const [isTestDatabase, setIsTestDatabase] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const profileDropdownRef = useRef(null);
  const tourGuideSettingSeededRef = useRef(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [toast, setToast] = useState({ message: '', type: '' });
  const [setupCompletion, setSetupCompletion] = useState({
    loaded: false,
    completedCount: 0,
    totalCount: SETUP_TASK_KEYS.length,
  });
  const [hasWorkOrderRecords, setHasWorkOrderRecords] = useState(false);

  useEffect(() => {
    registerErrorHandler(setToast);
  }, []);

  /* Toast error alert closes after 5 seconds */
  useEffect(() => {
    if (toast.message) {
      const timer = setTimeout(
        () => setToast({ message: '', type: '' }),
        toast.duration || 5000,
      );
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const hasPageAccess = useCallback(
    (page) => {
      if (!userProfile || !userProfile.pages) return true;
      return userProfile.pages.includes(page);
    },
    [userProfile]
  );
  // const isSuperUser = userProfile?.roleId === 1;
  const canViewApplicationDashboard = hasPageAccess('/dashboards/application');

  // Assign routes to start with sidebar minimised
  const [sidebarActive, seSidebarActive] = useState(() =>
    AUTO_COLLAPSED_ROUTES.includes(location.pathname)
  );

  const performClientLogout = useCallback((broadcast = true) => {
    // Clear all session storage
    sessionStorage.clear();
    // Remove cached profile
    sessionStorage.removeItem('cache_user-profile');
    // Remove token and allowed pages
    localStorage.removeItem("token");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("contractor_access_token");
    localStorage.removeItem("userPages");
    if (broadcast) {
      localStorage.setItem(LOGOUT_BROADCAST_KEY, String(Date.now()));
    }
    // Redirect to sign-in
    window.location.href = "/sign-in";
  }, []);

  const handleLogout = async () => {
    try {
      await logoutUser();
    } catch (error) {
      console.error('Error during logout request:', error);
    } finally {
      performClientLogout(true);
    }
  };

  const fetchUserProfile = async () => {
    try {
      const cacheKey = 'cache_user-profile';
      let data = sessionStorage.getItem(cacheKey);
      if (data) {
        data = JSON.parse(data);
      } else {
        data = await getUserProfile();
        sessionStorage.setItem(cacheKey, JSON.stringify(data));
      }
      const db = data.db.split('_')[1].replace(/^./, c => c.toUpperCase());

      setUserProfile(data);
      setUserImage(data.user_image);
      setName(data.name);
      setDatabase(db);
      setIsTestDatabase(Boolean(data.db && data.db.toLowerCase().endsWith('_test')));
      if (data.pages) {
        localStorage.setItem('userPages', JSON.stringify(data.pages));
      }
      return data;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  };

  // Load application settings
  const fetchSettings = useCallback(async () => {
    try {
      const data = await getSettingsByOrg();
      const settingsArray = Array.isArray(data) ? data : [];

      const mapped = settingsArray.reduce((acc, item) => {
        acc[item.name] = {
          value: item.value,
          description: item.description,
          type: item.type,
          options: item.options,
          id: item.id,
          organisationId: item.organisationId,
          createdAt: item.createdAt,
          updatedAt: item.updatedAt,
        };
        return acc;
      }, {});

      setAppSettings(mapped);

      setSettingsLoaded(true);
    } catch (err) {
      console.error('Error fetching settings:', err);
    }
  }, [setAppSettings]);

  useEffect(() => {
    if (!settingsLoaded || !userProfile || tourGuideSettingSeededRef.current) {
      return;
    }

    const hasTourGuideSetting = Object.prototype.hasOwnProperty.call(appSettings || {}, TOUR_GUIDE_SETTING.name);
    if (hasTourGuideSetting) {
      return;
    }

    const organisationId =
      userProfile?.org_id ?? userProfile?.organisationId ?? userProfile?.organisation?.id ?? null;
    if (!organisationId) {
      return;
    }

    tourGuideSettingSeededRef.current = true;
    (async () => {
      try {
        await createSettings({
          ...TOUR_GUIDE_SETTING,
          organisationId,
          customerSetting: 0,
        });
        await fetchSettings();
      } catch (seedError) {
        console.error('Error seeding tour guide setting:', seedError);
      }
    })();
  }, [appSettings, fetchSettings, settingsLoaded, userProfile]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    fetchUserProfile();
  }, []);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key !== LOGOUT_BROADCAST_KEY || !event.newValue) {
        return;
      }
      performClientLogout(false);
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [performClientLogout]);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key !== LOGIN_BROADCAST_KEY || !event.newValue) {
        return;
      }
      window.location.reload();
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  useEffect(() => {
    const handleSetupTasksUpdated = () => {
      if (userProfile) {
        fetchSetupCompletion();
      }
    };

    window.addEventListener('setup-tasks-updated', handleSetupTasksUpdated);
    return () => window.removeEventListener('setup-tasks-updated', handleSetupTasksUpdated);
  }, [userProfile]);

  const fetchSetupCompletion = useCallback(async () => {
    try {
      const tasks = await getSetupTasks();
      const completedKeys = new Set(tasks.map((task) => task.taskKey));
      const completedCount = SETUP_TASK_KEYS.filter((taskKey) => completedKeys.has(taskKey)).length;

      setSetupCompletion({
        loaded: true,
        completedCount,
        totalCount: SETUP_TASK_KEYS.length,
      });
    } catch (error) {
      console.error('Error fetching setup completion:', error);
      setSetupCompletion((current) => ({
        ...current,
        loaded: true,
      }));
    }
  }, []);

  useEffect(() => {
    if (userProfile) {
      fetchSetupCompletion();
    }
  }, [userProfile, fetchSetupCompletion]);

  const canProbeWorkOrders = useCallback(() => {
    if (!userProfile) return false;

    const privileges = Array.isArray(userProfile?.privileges) ? userProfile.privileges : [];
    const pages = Array.isArray(userProfile?.pages) ? userProfile.pages : [];

    const hasWorkOrdersPrivilege = privileges.includes('work-orders:view');
    const hasWorkOrdersPage = pages.includes('/work-orders');

    return hasWorkOrdersPrivilege || hasWorkOrdersPage;
  }, [userProfile]);

  const fetchWorkOrderPresence = useCallback(async () => {
    try {
      const response = await getWorkOrderCountSummary();

      const count = Number(response?.count) || 0;
      const hasMoreThanOne = Boolean(response?.hasMoreThanOne);

      setHasWorkOrderRecords(hasMoreThanOne || count > 0);
    } catch (error) {
      console.error('Error fetching work-order presence:', error);
      setHasWorkOrderRecords(false);
    }
  }, []);

  useEffect(() => {
    if (userProfile) {
      if (canProbeWorkOrders()) {
        fetchWorkOrderPresence();
      } else {
        setHasWorkOrderRecords(false);
      }
    }
  }, [canProbeWorkOrders, fetchWorkOrderPresence, userProfile]);

  const setupProgressPercent = setupCompletion.totalCount
    ? Math.round((setupCompletion.completedCount / setupCompletion.totalCount) * 100)
    : 0;
  const showSetupPrompt = setupCompletion.loaded && setupCompletion.completedCount < setupCompletion.totalCount;
  const showSetupPromptProfile = showSetupPrompt && !hasWorkOrderRecords;

  useEffect(() => {
    const handleDropdownClick = (event) => {
      event.preventDefault();
      const clickedLink = event.currentTarget;
      const clickedDropdown = clickedLink.closest(".dropdown");

      if (!clickedDropdown) return;

      const isActive = clickedDropdown.classList.contains("open");

      // Close all dropdowns
      const allDropdowns = document.querySelectorAll(".sidebar-menu .dropdown");
      allDropdowns.forEach((dropdown) => {
        dropdown.classList.remove("open");
        const submenu = dropdown.querySelector(".sidebar-submenu");
        if (submenu) {
          submenu.style.maxHeight = "0px"; // Collapse submenu
        }
      });

      // Toggle the clicked dropdown
      if (!isActive) {
        clickedDropdown.classList.add("open");
        const submenu = clickedDropdown.querySelector(".sidebar-submenu");
        if (submenu) {
          const height = submenu.scrollHeight;
          if (height > 0) {
            submenu.style.maxHeight = `${height}px`;
          } else {
            // When the menu is collapsed, scrollHeight will be zero. Allow
            // natural height so the submenu is visible when the sidebar expands.
            submenu.style.maxHeight = 'none';
          }
        }
      }
    };

    // Attach click event listeners to all dropdown triggers
    const dropdownTriggers = document.querySelectorAll(
      ".sidebar-menu .dropdown > a, .sidebar-menu .dropdown > Link"
    );

    dropdownTriggers.forEach((trigger) => {
      trigger.addEventListener("click", handleDropdownClick);
    });

    const openActiveDropdown = () => {
      const allDropdowns = document.querySelectorAll(".sidebar-menu .dropdown");
      allDropdowns.forEach((dropdown) => {
        const submenuLinks = dropdown.querySelectorAll(".sidebar-submenu li a");
        submenuLinks.forEach((link) => {
          if (
            link.getAttribute("href") === location.pathname ||
            link.getAttribute("to") === location.pathname
          ) {
            dropdown.classList.add("open");
            const submenu = dropdown.querySelector(".sidebar-submenu");
            if (submenu) {
              const height = submenu.scrollHeight;
              if (height > 0) {
                submenu.style.maxHeight = `${height}px`;
              } else {
                // When sidebar is collapsed the submenu might be hidden and
                // scrollHeight will be zero. Remove the maxHeight restriction so
                // the submenu can expand properly on hover.
                submenu.style.maxHeight = 'none';
              }
            }
          }
        });
      });
    };

    // Open the submenu that contains the active route
    openActiveDropdown();

    // Cleanup event listeners on unmount
    return () => {
      dropdownTriggers.forEach((trigger) => {
        trigger.removeEventListener("click", handleDropdownClick);
      });
    };
  }, [location.pathname]);


  let sidebarControl = () => {
    seSidebarActive(!sidebarActive);
  };

  let mobileMenuControl = () => {
    setMobileMenu(!mobileMenu);
  };

  const toggleProfileDropdown = () => {
    setProfileDropdownOpen(!profileDropdownOpen);
  };

  const closeProfileDropdown = () => {
    setProfileDropdownOpen(false);
  };

  const handleProductTourFromProfile = () => {
    const triggerValue = String(Date.now());
    sessionStorage.setItem(PRODUCT_TOUR_TRIGGER_ONCE_KEY, triggerValue);
    localStorage.removeItem(PRODUCT_TOUR_TRIGGER_KEY);
    window.dispatchEvent(new CustomEvent('product-tour-triggered', { detail: { triggerValue } }));
    closeProfileDropdown();
    navigate('/home');
  };

  useEffect(() => {
    seSidebarActive(AUTO_COLLAPSED_ROUTES.includes(location.pathname));
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        profileDropdownRef.current &&
        !profileDropdownRef.current.contains(event.target)
      ) {
        setProfileDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const hideTopMenuRight =
    location.pathname === '/dashboards/downtime';
  // || location.pathname === '/dashboards/performance';

  const isBetaReleasePage = BETA_RELEASE_PAGE.includes(location.pathname);

  const isShiftLogModuleEnabled = useModule('shiftlog');
  const useRotables = useModule('rotables');
  const isLccModuleEnabled = useModule('lcc');
  const canViewShiftLogs = !!userProfile?.privileges?.includes('shift-log:view');
  const toastType = toast.type || 'danger';
  const toastStyleMap = {
    success: {
      container: 'bg-success-100',
      text: 'text-success',
    },
    warning: {
      container: 'bg-warning-100',
      text: 'text-warning',
    },
    danger: {
      container: 'bg-danger-100',
      text: 'text-danger',
    },
  };
  const activeToastStyle = toastStyleMap[toastType] || toastStyleMap.danger;

  return (
    <UserProfileContext.Provider value={{ userProfile, hasPageAccess, hasWorkOrderRecords, setupCompletion, showSetupPrompt }}>
      {toast.message && (
        <div
          className="toast-container position-fixed top-0 start-50 translate-middle-x p-3"
          style={{ zIndex: 2000 }}
        >
          <div
            className={`toast show ${activeToastStyle.container} border-0 p-12`}
          >
            <div className="d-flex align-items-center justify-content-between">
              <div
                className={`toast-body fw-semibold m-0 ${activeToastStyle.text}`}
              >
                {toast.message}
              </div>
              <button
                type="button"
                className="btn-close ms-3 me-2"
                aria-label="Close"
                onClick={() => setToast({ message: '', type: '' })}
              ></button>
            </div>
          </div>
        </div>
      )}
      <section className={mobileMenu ? "overlay active" : "overlay"}>
        {/* Sidebar */}
        <aside
          className={
            sidebarActive
              ? "sidebar active"
              : mobileMenu
                ? "sidebar sidebar-open"
                : "sidebar"
          }
        >
          <button
            onClick={mobileMenuControl}
            type='button'
            className='sidebar-close-btn'
          >
            <Icon icon='radix-icons:cross-2' />
          </button>
          <div>
            <Link to='/home' className='sidebar-logo'>
              <img
                src='/assets/images/logo.png'
                alt='site logo'
                className='light-logo ps-16 pe-24 py-24'
                style={{ maxHeight: '100px' }}
              />
              <img
                src='/assets/images/logo-light.png'
                alt='site logo'
                className='dark-logo py-20'
              />
              <img
                src='/assets/images/logo-icon.png'
                alt='site logo'
                className='logo-icon'
              />
            </Link>
          </div>
          <div className='sidebar-menu-area'>
            <ul className='sidebar-menu' id='sidebar-menu'>
              <li>
                <NavLink
                  to='/home'
                  className={(navData) => (navData.isActive ? 'active-page' : '')}
                >
                  <Icon icon='mage:home-3' className='menu-icon' />
                  <span>Home</span>
                </NavLink>
              </li>
              {
                (hasPageAccess('/dashboards/overview') ||
                  canViewApplicationDashboard ||
                  hasPageAccess('/dashboards/work-requests') ||
                  hasPageAccess('/dashboards/work-orders') ||
                  hasPageAccess('/dashboards/forms') ||
                  hasPageAccess('/dashboards/utilisation') ||
                  hasPageAccess('/dashboards/performance') ||
                  hasPageAccess('/dashboards/reliability') ||
                  hasPageAccess('/reports/utilisation-report')) && (
                <li className='dropdown'>
                  <Link
                    to='#'
                  >
                    <Icon icon='mage:dashboard-check' className='menu-icon' />
                    <span>Dashboards</span>
                    </Link>
                    <ul className='sidebar-submenu py-0'>
                      {hasPageAccess('/dashboards/overview') && (
                        <li>
                          <NavLink
                            to='/dashboards/overview'
                            className={(navData) => navData.isActive ? 'active-page' : ''}
                          >
                            <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                            Overview
                          </NavLink>
                        </li>
                      )}
                      {canViewApplicationDashboard && (
                        <li>
                          <NavLink
                            to='/dashboards/application'
                            className={(navData) => navData.isActive ? 'active-page' : ''}
                          >
                            <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                            Application
                          </NavLink>
                        </li>
                      )}
                      {hasPageAccess('/dashboards/work-requests') && (
                        <li>
                          <NavLink
                            to='/dashboards/work-requests'
                            className={(navData) => navData.isActive ? 'active-page' : ''}
                          >
                            <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                            Work Requests
                          </NavLink>
                        </li>
                      )}
                      {hasPageAccess('/dashboards/work-orders') && (
                        <li>
                          <NavLink
                            to='/dashboards/work-orders'
                            className={(navData) => navData.isActive ? 'active-page' : ''}
                          >
                            <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                            Work Orders
                          </NavLink>
                        </li>
                      )}
                      {hasPageAccess('/dashboards/forms') && (
                        <li>
                          <NavLink
                            to='/dashboards/forms'
                            className={(navData) => navData.isActive ? 'active-page' : ''}
                          >
                            <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                            Forms
                          </NavLink>
                        </li>
                      )}
                      {hasPageAccess('/dashboards/downtime') && (
                        <li>
                          <NavLink
                            to='/dashboards/downtime'
                            className={(navData) => navData.isActive ? 'active-page' : ''}
                          >
                            <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                            Downtime
                          </NavLink>
                        </li>
                      )}
                      {hasPageAccess('/dashboards/operational') && (
                        <li>
                          <NavLink
                            to='/dashboards/operational'
                            className={(navData) => navData.isActive ? 'active-page' : ''}
                          >
                            <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                            Operational
                          </NavLink>
                        </li>
                      )}
                      {hasPageAccess('/dashboards/performance') && (
                        <li>
                          <NavLink
                            to='/dashboards/performance'
                            className={(navData) => navData.isActive ? 'active-page' : ''}
                          >
                            <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                            Performance Dashboard
                          </NavLink>
                        </li>
                      )}
                      {hasPageAccess('/dashboards/utilisation') && (
                        <li>
                          <NavLink
                            to='/dashboards/utilisation'
                            className={(navData) => navData.isActive ? 'active-page' : ''}
                          >
                            <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                            Utilisation
                          </NavLink>
                        </li>
                      )}
                      {hasPageAccess('/dashboards/reliability') && (
                        <li>
                          <NavLink
                            to='/dashboards/reliability'
                            className={(navData) => navData.isActive ? 'active-page' : ''}
                          >
                            <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                            Reliability
                          </NavLink>
                        </li>
                      )}
                    </ul>
                  </li>
                )}

              {(hasPageAccess('/asset-list') || hasPageAccess('/area')) && (
                <li className='dropdown'>
                  <Link
                    to='#'
                    data-tg-tour="Assets and areas are where you manage the physical things your team maintains and the places they work."
                    data-tg-group="home-tour"
                    data-tg-order="1"
                    data-tg-title="Assets and areas"
                  >
                    <Icon icon='mage:delivery-truck' className='menu-icon' />
                    <span>Assets &amp; Areas</span>
                  </Link>
                  <ul className='sidebar-submenu py-0'>
                    {hasPageAccess('/asset-list') && (
                      <li>
                        <NavLink
                          to='/asset-list'
                          className={(navData) => (navData.isActive ? 'active-page' : '')}
                        >
                          <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                          Assets
                        </NavLink>
                      </li>
                    )}
                    {hasPageAccess('/area') && (
                      <li>
                        <NavLink
                          to='/area'
                          className={(navData) => (navData.isActive ? 'active-page' : '')}
                        >
                          <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                          Areas
                        </NavLink>
                      </li>
                    )}
                  </ul>
                </li>
              )}

              {(hasPageAccess('/work-orders') ||
                hasPageAccess('/work-assignment') ||
                hasPageAccess('/visual-planner') ||
                hasPageAccess('/notifications') ||
                hasPageAccess('/events') ||
                hasPageAccess('/compliance-management') ||
                hasPageAccess('/component-management') ||
                hasPageAccess('/service-management') ||
                (isShiftLogModuleEnabled && hasPageAccess('/shift-logs'))) && (
                  <li className='dropdown'>
                    <Link
                      to='#'
                      data-tg-tour="Work Management is where you handle work orders, requests, events, and day-to-day execution."
                      data-tg-group="home-tour"
                      data-tg-order="2"
                      data-tg-title="Work management"
                    >
                      <Icon icon='mage:clipboard-2' className='menu-icon' />
                      <span>Work Management</span>
                    </Link>
                    <ul className='sidebar-submenu py-0'>
                      {hasPageAccess('/notifications') && (
                        <li>
                          <NavLink
                            to='/notifications'
                            className={(navData) => (navData.isActive ? 'active-page' : '')}
                          >
                            <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                            Work Requests
                          </NavLink>
                        </li>
                      )}
                      {hasPageAccess('/work-orders') && (
                        <li>
                          <NavLink
                            to='/work-orders'
                            className={(navData) => (navData.isActive ? 'active-page' : '')}
                          >
                            <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                            Work Orders
                          </NavLink>
                        </li>
                      )}
                      {hasPageAccess('/work-assignment') && (
                        <li>
                          <NavLink
                            to='/work-assignment'
                            className={(navData) => (navData.isActive ? 'active-page' : '')}
                          >
                            <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                            Work Assignment
                            <span className="badge bg-primary">BETA</span>
                          </NavLink>
                        </li>
                      )}
                      {hasPageAccess('/events') && (
                        <li>
                          <NavLink
                            to='/events'
                            className={(navData) => (navData.isActive ? 'active-page' : '')}
                          >
                            <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                            Events
                          </NavLink>
                        </li>
                      )}
                      {hasPageAccess('/visual-planner') && (
                        <li>
                          <NavLink
                            to='/visual-planner'
                            className={(navData) => (navData.isActive ? 'active-page' : '')}
                          >
                            <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                            Events Planner
                          </NavLink>
                        </li>
                      )}
                      {isShiftLogModuleEnabled && canViewShiftLogs && hasPageAccess('/shift-logs') && (
                        <li>
                          <NavLink
                            to='/shift-logs'
                            className={(navData) => (navData.isActive ? 'active-page' : '')}
                          >
                            <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                            Shift Log

                          </NavLink>
                        </li>
                      )}
                    </ul>
                  </li>
                )}

              {(hasPageAccess('/component-management') ||
                hasPageAccess('/component-planner') ||
                hasPageAccess('/component-analysis') ||
                hasPageAccess('/compliance-management') ||
                hasPageAccess('/service-management') ||
                hasPageAccess('/inspections-management')) && (
                  <li className='dropdown'>
                    <Link
                      to='#'
                      data-tg-tour="Task Management keeps the maintenance workflow organised, from compliance and servicing to inspections and component replacements."
                      data-tg-group="home-tour"
                      data-tg-order="3"
                      data-tg-title="Task management"
                    >
                      <Icon icon='mdi:clipboard-list-outline' className='menu-icon' />
                      <span>Task Management</span>
                    </Link>
                    <ul className='sidebar-submenu py-0'>
                      {hasPageAccess('/component-management') && (
                        <li>
                          <NavLink
                            to='/component-management'
                            className={(navData) => (navData.isActive ? 'active-page' : '')}
                          >
                            <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                            Components
                          </NavLink>
                        </li>
                      )}
                      {hasPageAccess('/compliance-management') && (
                        <li>
                          <NavLink
                            to='/compliance-management'
                            className={(navData) => (navData.isActive ? 'active-page' : '')}
                          >
                            <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                            Compliance
                          </NavLink>
                        </li>
                      )}
                      {(hasPageAccess('/service-management') || hasPageAccess('/component-management')) && (
                        <li>
                          <NavLink
                            to='/service-management'
                            className={(navData) => (navData.isActive ? 'active-page' : '')}
                          >
                            <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                            Servicing
                          </NavLink>
                        </li>
                      )}
                      {(hasPageAccess('/inspections-management') || hasPageAccess('/component-management')) && (
                        <li>
                          <NavLink
                            to='/inspections-management'
                            className={(navData) => (navData.isActive ? 'active-page' : '')}
                          >
                            <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                            Inspections
                          </NavLink>
                        </li>
                      )}
                      {hasPageAccess('/component-planner') && (
                        <li>
                          <NavLink
                            to='/component-planner'
                            className={(navData) => (navData.isActive ? 'active-page' : '')}
                          >
                            <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                            Component Planner
                          </NavLink>
                        </li>
                      )}
                      {hasPageAccess('/component-analysis') && (
                        <li>
                          <NavLink
                            to='/component-analysis'
                            className={(navData) => (navData.isActive ? 'active-page' : '')}
                          >
                            <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                            Component Analysis
                          </NavLink>
                        </li>
                      )}
                    </ul>
                  </li>
                )}

              {(hasPageAccess('/purchase-orders') || hasPageAccess('/supplier-list') || hasPageAccess('/approval-workflow-configuration')) && (
                <li className='dropdown'>
                  <Link
                    to='#'
                    data-tg-tour="Purchasing is where you can create purchase orders and manage suppliers, while still supporting teams that use an external purchasing system."
                    data-tg-group="home-tour"
                    data-tg-order="4"
                    data-tg-title="Purchasing"
                  >
                    <Icon icon='mdi:dollar' className='menu-icon' />
                    <span>Purchasing</span>
                  </Link>
                  <ul className='sidebar-submenu py-0'>
                    {hasPageAccess('/purchase-orders') && (
                      <li>
                        <NavLink
                          to='/purchase-orders'
                          className={(navData) => (navData.isActive ? 'active-page' : '')}
                        >
                          <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                          Purchase Orders
                        </NavLink>
                      </li>
                    )}
                    {hasPageAccess('/approval-workflow-configuration') && (
                      <li>
                        <NavLink
                          to='/approval-workflow-configuration'
                          className={(navData) => (navData.isActive ? 'active-page' : '')}
                        >
                          <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                          Approvals Setup
                        </NavLink>
                      </li>
                    )}
                    {hasPageAccess('/supplier-list') && (
                      <li>
                        <NavLink
                          to='/supplier-list'
                          className={(navData) => (navData.isActive ? 'active-page' : '')}
                        >
                          <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                          Suppliers
                        </NavLink>
                      </li>
                    )}
                  </ul>
                </li>
              )}

              {/* Parts Dropdown */}
              {hasPageAccess('/inventory') && (
                <li className='dropdown'>
                  <Link
                    to='#'
                    data-tg-tour="Inventory and parts help you track stock, warehouses, and the items your team needs on hand."
                    data-tg-group="home-tour"
                    data-tg-order="5"
                    data-tg-title="Inventory and parts"
                  >
                    <Icon
                      icon='mage:shopping-cart'
                      className='menu-icon'
                    />
                    <span>Inventory & Parts</span>
                  </Link>
                  <ul className='sidebar-submenu py-0'>
                    {hasPageAccess('/parts-list') && (
                      <li>
                        <NavLink
                          to='/parts-list'
                          className={(navData) =>
                            navData.isActive ? "active-page" : ""
                          }
                        >
                          <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                          Parts Catalogue
                        </NavLink>
                      </li>
                    )}
                    {hasPageAccess('/stock-items') && (
                      <li>
                        <NavLink
                          to='/stock-items'
                          className={(navData) =>
                            navData.isActive ? "active-page" : ""
                          }
                        >
                          <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                          Stock Items
                        </NavLink>
                      </li>
                    )}
                    {hasPageAccess('/rotables') && useRotables && (
                      <li>
                        <NavLink
                          to='/rotables'
                          className={(navData) =>
                            navData.isActive ? "active-page" : ""
                          }
                        >
                          <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                          Rotables
                        </NavLink>
                      </li>
                    )}
                    {hasPageAccess('/stocktakes') && (
                      <li>
                        <NavLink
                          to='/stocktakes'
                          className={(navData) =>
                            navData.isActive ? "active-page" : ""
                          }
                        >
                          <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                          Stocktakes
                        </NavLink>
                      </li>
                    )}
                    {hasPageAccess('/warehouse-list') && (
                      <li>
                        <NavLink
                          to='/warehouse-list'
                          className={(navData) =>
                            navData.isActive ? "active-page" : ""
                          }
                        >
                          <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                          Warehouses
                        </NavLink>
                      </li>
                    )}
                  </ul>
                </li>)}

              {(hasPageAccess('/forms') || hasPageAccess('/form-history')) && (
                <li className='dropdown'>
                  <Link
                    to='#'
                    data-tg-tour="Forms let you create digital sheets that directly integrate with the CMMS and can be executed in the field on the mobile app, capturing information directly at the source."
                    data-tg-group="home-tour"
                    data-tg-order="6"
                    data-tg-title="Forms"
                  >
                    <Icon icon='mdi:clipboard-edit-outline' className='menu-icon' />
                    <span>Forms</span>
                  </Link>
                  <ul className='sidebar-submenu py-0'>
                    {hasPageAccess('/forms') && (
                      <li>
                        <NavLink
                          to='/forms'
                          className={(navData) => navData.isActive ? 'active-page' : ''}
                        >
                          <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                          Form Library
                        </NavLink>
                      </li>
                    )}
                    {hasPageAccess('/form-history') && (
                      <li>
                        <NavLink
                          to='/form-history'
                          className={(navData) => navData.isActive ? 'active-page' : ''}
                        >
                          <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                          Forms Management
                        </NavLink>
                      </li>
                    )}
                  </ul>
                </li>
              )}

              {hasPageAccess('/maintenance-plans') && (
                <li className='dropdown'>
                  <Link to='#'>
                    <Icon icon='mdi:application-cog-outline' className='menu-icon' />
                    <span>Templates</span>
                  </Link>
                  <ul className='sidebar-submenu py-0'>
                    <li>
                      <NavLink
                        to='/maintenance-plans'
                        className={(navData) => (navData.isActive ? 'active-page' : '')}
                      >
                        <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                        Task Templates
                      </NavLink>
                    </li>
                  </ul>
                </li>
              )}

              {/* Plant Management Dropdown */}
              {(hasPageAccess('/asset-assignments') || hasPageAccess('/plant-rates')) && (
                <li className='dropdown'>
                  <Link to='#'>
                    <Icon icon='iconoir:laptop-fix' className='menu-icon' />
                    <span>Plant Management</span>
                  </Link>
                  <ul className='sidebar-submenu py-0'>
                    {hasPageAccess('/asset-assignments') && (
                      <li>
                        <NavLink
                          to='/asset-assignments'
                          className={(navData) => navData.isActive ? 'active-page' : ''}
                        >
                          <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                          Asset Assignment
                        </NavLink>
                      </li>
                    )}
                    {hasPageAccess('/plant-rates') && (
                      <li>
                        <NavLink
                          to='/plant-rates'
                          className={(navData) => navData.isActive ? 'active-page' : ''}
                        >
                          <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                          Plant Rates
                        </NavLink>
                      </li>
                    )}
                  </ul>
                </li>
              )}

              {(hasPageAccess('/reports') || hasPageAccess('/reports/utilisation-report')) && (
                <li className='dropdown'>
                    <Link
                      to='#'
                      data-tg-tour="Reports let you create custom reports that can be exported to excel, and schedule reports to automatically be emailed out."
                      data-tg-group="home-tour"
                      data-tg-order="7"
                      data-tg-title="Reports"
                    >
                      <Icon icon='mage:file-2' className='menu-icon' />
                      <span>Reports</span>
                    </Link>
                  <ul className='sidebar-submenu py-0'>
                    {hasPageAccess('/reports') && (
                      <li>
                        <NavLink
                          to='/reports'
                          className={(navData) => navData.isActive ? 'active-page' : ''}
                        >
                          <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                          Run Reports
                        </NavLink>
                      </li>
                    )}
                    {hasPageAccess('/reports') && (
                      <li>
                        <NavLink
                          to='/custom-reports'
                          className={(navData) => navData.isActive ? 'active-page' : ''}
                        >
                          <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                          Custom Reports
                        </NavLink>
                      </li>
                    )}
                  </ul>
                </li>
              )}

              {isLccModuleEnabled && hasPageAccess('/budgeting') && (
                <li>
                  <NavLink
                    to='/budgeting'
                    className={(navData) => (navData.isActive ? 'active-page' : '')}
                  >
                    <Icon icon='mdi:cash-multiple' className='menu-icon' />
                    <span>Budgeting</span>
                  </NavLink>
                </li>
              )}

              <hr style={{ borderTop: '1px solid #000', margin: '12px 0' }} />
              {(hasPageAccess('/hierarchy-structure-list') ||
                hasPageAccess('/teams-list') ||
                hasPageAccess('/technicians-list') ||
                hasPageAccess('/contractors')) && (
                  <li className='dropdown'>
                    <Link to='#'>
                      <Icon icon='mage:building-a' className='menu-icon' />
                      <span>Departments &amp; People</span>
                    </Link>
                    <ul className='sidebar-submenu py-0'>
                      {hasPageAccess('/hierarchy-structure-list') && (
                        <li>
                          <NavLink
                            to='/hierarchy-structure-list'
                            className={(navData) => (navData.isActive ? 'active-page' : '')}
                          >
                            <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                            Sites & Departments
                          </NavLink>
                        </li>
                      )}
                      {hasPageAccess('/teams-list') && (
                        <li>
                          <NavLink
                            to='/teams-list'
                            className={(navData) => (navData.isActive ? 'active-page' : '')}
                          >
                            <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                            Work Teams
                          </NavLink>
                        </li>
                      )}
                      {hasPageAccess('/technicians-list') && (
                        <li>
                          <NavLink
                            to='/technicians-list'
                            className={(navData) => (navData.isActive ? 'active-page' : '')}
                          >
                            <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                            Technicians
                          </NavLink>
                        </li>
                      )}
                      {hasPageAccess('/contractors') && (
                        <li>
                          <NavLink
                            to='/contractors'
                            className={(navData) => (navData.isActive ? 'active-page' : '')}
                          >
                            <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                            Contractors
                          </NavLink>
                        </li>
                      )}
                    </ul>
                  </li>
                )}
              {(hasPageAccess('/user-list') || hasPageAccess('/email-group-list')) && (
                <li className='dropdown'>
                  <Link to='#'>
                    <Icon icon='mdi:people-group-outline' className='menu-icon' />
                    <span>User &amp; Groups</span>
                  </Link>
                  <ul className='sidebar-submenu py-0'>
                    {hasPageAccess('/user-list') && (
                      <li>
                        <NavLink
                          to='/user-list'
                          className={(navData) => (navData.isActive ? 'active-page' : '')}
                        >
                          <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                          Users
                        </NavLink>
                      </li>
                    )}
                    {hasPageAccess('/email-group-list') && (
                      <li>
                        <NavLink
                          to='/email-group-list'
                          className={(navData) => (navData.isActive ? 'active-page' : '')}
                        >
                          <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                          Email Groups
                        </NavLink>
                      </li>
                    )}
                  </ul>
                </li>
              )}
              {(hasPageAccess('/meters') ||
                hasPageAccess('/asset-meters') ||
                hasPageAccess('/meter-readings') ||
                hasPageAccess('/measurement-units')) && (
                  <li className='dropdown'>
                    <Link
                      to='#'
                      data-tg-tour="Readings and meters is where you capture usage data and keep your equipment measurements up to date."
                      data-tg-group="home-tour"
                      data-tg-order="8"
                      data-tg-title="Readings and meters"
                    >
                      <Icon icon='mage:health-circle' className='menu-icon' />
                      <span>Readings &amp; Meters</span>
                    </Link>
                    <ul className='sidebar-submenu py-0'>
                      {hasPageAccess('/meter-readings') && (
                        <li>
                          <NavLink
                            to='/meter-readings'
                            className={(navData) => (navData.isActive ? 'active-page' : '')}
                          >
                            <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                            Meter Readings
                          </NavLink>
                        </li>
                      )}
                      {hasPageAccess('/meters') && (
                        <li>
                          <NavLink
                            to='/meters'
                            className={(navData) => (navData.isActive ? 'active-page' : '')}
                          >
                            <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                            Meters
                          </NavLink>
                        </li>
                      )}
                      {/* {hasPageAccess('/asset-meters') && (
                        <li>
                          <NavLink
                            to='/asset-meters'
                            className={(navData) => (navData.isActive ? 'active-page' : '')}
                          >
                            <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                            Assign Meters
                          </NavLink>
                        </li>
                      )} */}
                      {hasPageAccess('/measurement-units') && (
                        <li>
                          <NavLink
                            to='/measurement-units'
                            className={(navData) => (navData.isActive ? 'active-page' : '')}
                          >
                            <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                            Units of Measure
                          </NavLink>
                        </li>
                      )}
                    </ul>
                  </li>
                )}

              {/* Reference Tables Dropdown */}
              <li className='dropdown'>
                <Link to='#'>
                  <Icon
                    icon='mage:book-text'
                    className='menu-icon'
                  />
                  <span>Reference Tables</span>
                </Link>
                <ul className='sidebar-submenu py-0'>
                  {hasPageAccess('/organisation') && (
                    <li>
                      <NavLink
                        to='/organisation'
                        className={(navData) =>
                          navData.isActive ? 'active-page' : ''
                        }
                      >
                        <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                        Organisation
                      </NavLink>
                    </li>
                  )}
                  {hasPageAccess('/asset-reference') && (
                    <li>
                      <NavLink
                        to='/asset-reference'
                        className={(navData) => (navData.isActive ? 'active-page' : '')}
                      >
                        <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                        Asset Tables
                      </NavLink>
                    </li>
                  )}
                  {hasPageAccess('/event-location') && (
                    <li>
                      <NavLink
                        to='/event-location'
                        className={(navData) => (navData.isActive ? 'active-page' : '')}
                      >
                        <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                        Event Tables
                      </NavLink>
                    </li>
                  )}
                  {hasPageAccess('/group-list') && (
                    <li>
                      <NavLink
                        to='/group-list'
                        className={(navData) => (navData.isActive ? 'active-page' : '')}
                      >
                        <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                        Models &amp; Groups
                      </NavLink>
                    </li>
                  )}
                  {hasPageAccess('/work-order-reference') && (
                    <li>
                      <NavLink
                        to='/work-order-reference'
                        className={(navData) => (navData.isActive ? 'active-page' : '')}
                      >
                        <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                        Work Order Tables
                      </NavLink>
                    </li>
                  )}
                  {hasPageAccess('/finance-reference') && (
                    <li>
                      <NavLink
                        to='/finance-reference'
                        className={(navData) => (navData.isActive ? 'active-page' : '')}
                      >
                        <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                        Finance Tables
                      </NavLink>
                    </li>
                  )}
                  {hasPageAccess('/purchasing-reference') && (
                    <li>
                      <NavLink
                        to='/purchasing-reference'
                        className={(navData) => (navData.isActive ? 'active-page' : '')}
                      >
                        <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                        Inventory Tables
                      </NavLink>
                    </li>
                  )}
                  {hasPageAccess('/task-reference') && (
                    <li>
                      <NavLink
                        to='/task-reference'
                        className={(navData) => (navData.isActive ? 'active-page' : '')}
                      >
                        <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                        Task Tables
                      </NavLink>
                    </li>
                  )}
                  {hasPageAccess('/form-reference') && (
                    <li>
                      <NavLink
                        to='/form-reference'
                        className={(navData) => (navData.isActive ? 'active-page' : '')}
                      >
                        <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                        Form Tables
                      </NavLink>
                    </li>
                  )}
                  {hasPageAccess('/project-reference') && (
                    <li>
                      <NavLink
                        to='/project-reference'
                        className={(navData) => (navData.isActive ? 'active-page' : '')}
                      >
                        <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                        Plant Management
                      </NavLink>
                    </li>
                  )}
                  {hasPageAccess('/global-reference') && (
                    <li>
                      <NavLink
                        to='/global-reference'
                        className={(navData) => (navData.isActive ? 'active-page' : '')}
                      >
                        <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                        Global Tables
                      </NavLink>
                    </li>
                  )}
                </ul>
              </li>

              {/* Data Loading Dropdown */}
              {hasPageAccess('/data-loading') && (
                <li>
                  <NavLink
                    to='/data-loading'
                    className={(navData) => (navData.isActive ? 'active-page' : '')}
                  >
                    <Icon icon='mage:database-2' className='menu-icon' />
                    <span>Data Loading</span>
                  </NavLink>
                </li>
              )}

              {/* Settings Dropdown */}
              {hasPageAccess('/settings') && (
                <li className='dropdown'>
                  <Link to='#'>
                    <Icon
                      icon='icon-park-outline:setting-two'
                      className='menu-icon'
                    />
                    <span>Settings</span>
                  </Link>
                  <ul className='sidebar-submenu py-0'>
                    {hasPageAccess('/settings') && (
                      <li>
                        <NavLink
                          to='/settings'
                          className={(navData) =>
                            navData.isActive ? "active-page" : ""
                          }
                        >
                          <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                          App Settings
                        </NavLink>
                      </li>
                    )}
                    {hasPageAccess('/privileges') && (
                      <li>
                        <NavLink
                          to='/privileges'
                          className={(navData) =>
                            navData.isActive ? "active-page" : ""
                          }
                        >
                          <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                          User Privileges
                        </NavLink>
                      </li>
                    )}
                    {hasPageAccess('/integrations') && (
                      <li>
                        <NavLink
                          to='/integrations'
                          className={(navData) =>
                            navData.isActive ? "active-page" : ""
                          }
                        >
                          <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                          Integrations
                        </NavLink>
                      </li>
                    )}
                    {hasPageAccess('/roles') && (
                      <li>
                        <NavLink
                          to='/roles'
                          className={(navData) =>
                            navData.isActive ? "active-page" : ""
                          }
                        >
                          <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                          Roles
                        </NavLink>
                      </li>
                    )}
                    {hasPageAccess('/api-docs') && (
                      <li>
                        <NavLink
                          to='/api-docs'
                          className={(navData) =>
                            navData.isActive ? "active-page" : ""
                          }
                        >
                          <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                          API Documentation
                        </NavLink>
                      </li>
                    )}
                    {hasPageAccess('/llm-config') && (
                      <li>
                        <NavLink
                          to='/llm-config'
                          className={(navData) =>
                            navData.isActive ? "active-page" : ""
                          }
                        >
                          <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                          LLM Configuration
                        </NavLink>
                      </li>
                    )}
                    {hasPageAccess('/setup') && (
                      <li>
                        <NavLink
                          to='/setup'
                          data-tg-tour="Use Setup to finish onboarding and create the core records your team needs."
                          data-tg-group="setup-tour"
                          data-tg-order="1"
                          data-tg-title="Setup navigation"
                          className={(navData) =>
                            navData.isActive ? "active-page" : ""
                          }
                        >
                          <i className='ri-circle-fill circle-icon text-secondary-600 w-auto' />{' '}
                          Onboarding Wizard
                        </NavLink>
                      </li>
                    )}
                  </ul>
                </li>)}
            </ul>
          </div>
        </aside>

        <main
          className={sidebarActive ? "dashboard-main active" : "dashboard-main"}
        >
          <div className="d-flex flex-column min-vh-100">
            {/* Top navbar */}
            <div className={`navbar-header ${isTestDatabase ? 'bg-warning' : ''}`}>
              <div className='row align-items-center w-100 justify-content-between'>
                <div className='col-auto'>
                  <div className='d-flex flex-wrap align-items-center gap-4'>
                    <button
                      type='button'
                      className='sidebar-toggle'
                      onClick={sidebarControl}
                    >
                      {sidebarActive ? (
                        <Icon
                          icon='mdi:arrow-collapse-right'
                          className='icon text-2xl non-active'
                        />
                      ) : (
                        <Icon
                          icon='mdi:arrow-collapse-left'
                          className='icon text-2xl non-active '
                        />
                      )}
                    </button>

                    {pageTitle && (
                      <h5 className='fw-semibold mb-0 page-title'>{pageTitle}</h5>
                    )}
                    {isBetaReleasePage && (
                      <div className='alert alert-warning py-2 px-3 mb-0 d-flex align-items-center gap-2'
                      style={{
                        position: 'fixed',
                        top: 0,
                        left: 'calc(50% - 300px)',
                        borderTopRightRadius: 0,
                        borderTopLeftRadius: 0,
                       }}>
                        <span className="badge bg-primary">BETA</span>
                        <span className='mb-0'>This page is in beta realease. Some functionality may be misssing or not work as expected.</span>
                      </div>
                    )}
                    {headerActions && (
                      <div className='ms-3 d-flex align-items-center'>
                        {headerActions}
                      </div>
                    )}

                    <button
                      onClick={mobileMenuControl}
                      type='button'
                      className='sidebar-mobile-toggle'
                    >
                      <Icon icon='heroicons:bars-3-solid' className='icon' />
                    </button>
                  </div>
                </div>
                {isTestDatabase && (
                  <div className='col d-flex justify-content-end'>
                    <span className='badge bg-warning text-dark text-uppercase fs-5 px-4 py-2'>
                      TEST SYSTEM
                    </span>
                  </div>
                )}
                {!hideTopMenuRight && (
                  <div className='col-auto'>
                    <div className='d-flex flex-wrap align-items-center gap-3'>


                      {/* Search Input */}
                      <div id='header-search-container' style={{ width: "300px" }} className='d-flex align-items-center ms-3' />
                      {headerRightActions && (
                        <div className='d-flex align-items-center'>
                          {headerRightActions}
                        </div>
                      )}
                      <div className='dropdown'>
                        <button
                          className='has-indicator w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center'
                          type='button'
                          data-bs-toggle='dropdown'
                        >
                          <Icon
                            icon='iconoir:bell'
                            className='text-primary-light text-xl'
                          />
                        </button>
                        <div className='dropdown-menu to-top dropdown-menu-lg p-0'>
                          <div className='m-16 py-12 px-16 radius-8 bg-primary-50 mb-16 d-flex align-items-center justify-content-between gap-2'>
                            <div>
                              <h6 className='text-lg text-primary-light fw-semibold mb-0'>
                                Notifications
                              </h6>
                            </div>
                            <span className='text-primary-600 fw-semibold text-lg w-40-px h-40-px rounded-circle bg-base d-flex justify-content-center align-items-center'>
                              0
                            </span>
                          </div>
                          <div className='max-h-400-px overflow-y-auto scroll-sm pe-4'>
                            {/*<Link
                        to='#'
                        className='px-24 py-12 d-flex align-items-start gap-3 mb-2 justify-content-between'
                       >
                        <div className='text-black hover-bg-transparent hover-text-primary d-flex align-items-center gap-3'>
                          <span className='w-44-px h-44-px bg-success-subtle text-success-main rounded-circle d-flex justify-content-center align-items-center flex-shrink-0'>
                            <Icon
                              icon='bitcoin-icons:verify-outline'
                              className='icon text-xxl'
                            />
                          </span>
                          <div>
                            <h6 className='text-md fw-semibold mb-4'>
                              Congratulations
                            </h6>
                            <p className='mb-0 text-sm text-secondary-light text-w-200-px'>
                              Your profile has been Verified. Your profile has
                              been Verified
                            </p>
                          </div>
                        </div>
                        <span className='text-sm text-secondary-light flex-shrink-0'>
                          23 Mins ago
                        </span>
                       </Link> */}
                          </div>
                          <div className='text-center py-12 px-16'>
                            {hasPageAccess('/notification') && (
                              <Link
                                to='/notification'
                                className='text-primary-600 fw-semibold text-md'
                              >
                                See All Notification
                              </Link>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className='dropdown' ref={profileDropdownRef}>
                        <button
                          className='d-flex justify-content-center align-items-center rounded-circle'
                          type='button'
                          onClick={toggleProfileDropdown}
                        >
                          <img
                            src={
                              userImage
                                ? `${process.env.REACT_APP_SPACES_BASE}/${userImage}`
                                : `${process.env.REACT_APP_SPACES_BASE}/user.png`
                            }
                            alt="image_user"
                            className="w-40-px h-40-px object-fit-cover rounded-circle"
                          />
                        </button>
                        <div className={
                          profileDropdownOpen
                            ? 'dropdown-menu dropdown-menu-start to-top dropdown-menu-sm show'
                            : 'dropdown-menu dropdown-menu-start to-top dropdown-menu-sm'
                        }
                          style={{ right: '0px' }}
                        >
                          <div className='py-12 px-16 radius-8 bg-primary-50 mb-16 d-flex align-items-center justify-content-between gap-2'>
                            <div>
                              <h6 className='text-lg text-primary-light fw-semibold mb-2'>
                                {name}
                              </h6>
                              <span className='text-secondary-light fw-medium text-sm'>
                                Admin | {database}
                              </span>
                            </div>
                            <button type='button' className='hover-text-danger' onClick={closeProfileDropdown}>
                              <Icon
                                icon='radix-icons:cross-1'
                                className='icon text-xl'
                              />
                            </button>
                          </div>
                          <ul className='to-top-list'>
                            {/* {showSetupPromptProfile && (
                              <li className="mb-3">
                                <Link
                                  to="/setup"
                                  className="dropdown-item text-black px-0 py-8 hover-bg-transparent hover-text-primary d-block"
                                >
                                  <div className="mb-2 d-flex justify-content-between align-items-center gap-2">
                                    <span className="fw-semibold text-sm">
                                      Complete onboarding
                                    </span>
                                    <span className="text-secondary-light text-xs">
                                      {setupProgressPercent}%
                                    </span>
                                  </div>
                                  <div className="progress progress-sm mb-2" style={{ height: '6px' }}>
                                    <div
                                      className="progress-bar bg-primary"
                                      style={{ width: `${setupProgressPercent}%` }}
                                    />
                                  </div>
                                  <span className="text-secondary-light text-xs">
                                    Continue setup
                                  </span>
                                </Link>
                              </li>
                            )} */}
                            <li>
                              {hasPageAccess('/view-profile') && (
                                <Link
                                  className='dropdown-item text-black px-0 py-8 hover-bg-transparent hover-text-primary d-flex align-items-center gap-3'
                                  to='/view-profile'
                                >
                                  <Icon
                                    icon='solar:user-linear'
                                    className='icon text-xl'
                                  />{' '}
                                  My Profile
                                </Link>
                              )}
                            </li>
                              <li>
                                <button
                                  type='button'
                                  className='dropdown-item text-black px-0 py-8 hover-bg-transparent hover-text-primary d-flex align-items-center gap-3 border-0 bg-transparent w-100 text-start'
                                  onClick={handleProductTourFromProfile}
                                >
                                  <Icon icon='mdi:compass-outline' className='icon text-xl' />
                                  Product Tour
                                </button>
                              </li>
                              <li>
                                <Link
                                  className='dropdown-item text-black px-0 py-8 hover-bg-transparent hover-text-danger d-flex align-items-center gap-3'
                                  to='#' onClick={handleLogout}
                                >
                                <Icon icon='lucide:power' className='icon text-xl' />{" "}
                                Log Out
                              </Link>
                            </li>
                          </ul>
                        </div>
                      </div>
                      {/* Profile dropdown end */}
                    </div>
                  </div>
                )}
              </div>
            </div>
            {/* dashboard-main-body */}
            <div className="dashboard-main-body flex-grow-1 p-0">{children}</div>

            {/* Footer section */}
            {/* <footer className="d-footer mt-auto">
          <div className="row align-items-center justify-content-between">
            <div className="col-auto">
              <p className="mb-0">
                © Copyright 2025. Asertor. All Rights Reserved
              </p>
            </div>
          </div>
        </footer> */}
          </div>
        </main>
      </section>

    </UserProfileContext.Provider>
  );
};

const MasterLayoutBody = (props) => (
  <ModuleProvider>
    <MasterLayoutBodyContent {...props} />
  </ModuleProvider>
);

export default MasterLayoutBody;
